import Service from '@ember/service';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import Consts from '../utils/constants';
import upperFirst from 'lodash.upperfirst';

export default class SelectionDataService extends Service {
    @service intl;
    @service partData;

    @alias('partData.adcs') adcs;
    @alias('partData.drivers') drivers;
    @alias('partData.muxParts') muxParts;

    selectedAdc = null;
    previousAdc = null;

    selectedDriver = null;
    previousDriver = null;

    selectedSourceType = null;
    selectedTopology = null;
    selectedLsbSettling = null;

    gain = null; // used in the calculation of essentiallyFollower and actualTopologyId

    init() {
        super.init();

        /**
         * TOPOLOGY data
         */
        this.set('topologyOptions', [
            { description: this.intl.t('topology.inverting').toString(), id: Consts.TOPOLOGY.INVERTING },
            { description: this.intl.t('topology.non_inverting').toString(), id: Consts.TOPOLOGY.NON_INVERTING },
            { description: this.intl.t('topology.follower').toString(), id: Consts.TOPOLOGY.FOLLOWER },
        ]);

        this.set(
            'defaultTopology',
            this.topologyOptions.find((option) => option.id === Consts.TOPOLOGY.FOLLOWER),
        );

        /**
         * SOURCE TYPE data
         */
        this.set('sourceTypeOptions', [
            { description: this.intl.t('source_type.se').toString(), id: Consts.SOURCE_TYPE.SE },
            { description: this.intl.t('source_type.diff').toString(), id: Consts.SOURCE_TYPE.DIFF },
        ]);

        this.set('sourceTypeSeId', this._findOneOptionId('sourceTypeOptions', 'source_type.se'));
        this.set('sourceTypeDiffId', this._findOneOptionId('sourceTypeOptions', 'source_type.diff'));
        this.set('defaultSourceType', this.sourceTypeOptions[this.sourceTypeSeId - 1]);

        /**
         * LSB SETTLING data
         */
        this.set('lsbSettlingOptions', [
            { description: this.intl.t('lsb_settling.one').toString(), id: 1 },
            { description: this.intl.t('lsb_settling.half').toString(), id: 2 },
            { description: this.intl.t('lsb_settling.quarter').toString(), id: 3 },
            { description: this.intl.t('lsb_settling.eighth').toString(), id: 4 },
            { description: this.intl.t('lsb_settling.sixteenth').toString(), id: 5 },
        ]);

        this.set('lsbSettlingOneId', this._findOneOptionId('lsbSettlingOptions', 'lsb_settling.one'));
        this.set('lsbSettlingHalfId', this._findOneOptionId('lsbSettlingOptions', 'lsb_settling.half'));
        this.set('lsbSettlingQuarterId', this._findOneOptionId('lsbSettlingOptions', 'lsb_settling.quarter'));
        this.set('lsbSettlingEighth', this._findOneOptionId('lsbSettlingOptions', 'lsb_settling.eighth'));
        this.set('lsbSettlingSixteenth', this._findOneOptionId('lsbSettlingOptions', 'lsb_settling.sixteenth'));
        this.set('defaultLsbSettling', this.lsbSettlingOptions[this.lsbSettlingOneId - 1]);
    }

    get defaultAdc() {
        return this.partData.defaults.adc;
    }

    get defaultDriver() {
        return this.partData.defaults.driver;
    }

    @computed('selectedSourceType.id', 'muxParts.@each.sourceType') get selectedMux() {
        let currentSourceType = Object.keys(Consts.SOURCE_TYPE)
            .map((key) => {
                return { id: Consts.SOURCE_TYPE[key], description: key };
            })
            .find((sourceType) => sourceType.id === this.selectedSourceType.id);

        return this.muxParts.find((mux) => mux.sourceType === currentSourceType.description);
    }

    // Use for logic where "follower" = true, or the conditions that would make "follower" true are true
    // (Topology of Non-inverting and Gain of 1)
    @computed('gain', 'selectedTopology.id') get essentiallyFollower() {
        let gain = this.gain;
        let topology = this.selectedTopology.id; // 4th column -- inverting and non-inverting (follower is special case for non-inv, gain=1, rf=0)
        if (!(topology || gain)) {
            return false;
        }
        return topology === Consts.TOPOLOGY.FOLLOWER || (topology === Consts.TOPOLOGY.NON_INVERTING && gain === 1);
    }

    @computed('selectedTopology.id', 'gain') get actualTopologyId() {
        let selectedTopology = this.selectedTopology.id,
            essentiallyFollower = this.essentiallyFollower;
        return essentiallyFollower ? Consts.TOPOLOGY.FOLLOWER : selectedTopology;
    }

    @computed('selectedAdc') get baseSelectedAdc() {
        let selectedAdc = this.selectedAdc;
        if (selectedAdc.hasCurrentVariant) {
            // find the base part (the part with no variant)
            return this.partData.adcs.find((adc) => adc.partCore === selectedAdc.partCore && !adc.hasCurrentVariant);
        }

        return selectedAdc;
    }

    @computed('selectedAdc') get hiZSelectedAdc() {
        let selectedAdc = this.selectedAdc;
        // TODO: how will this be impacted if there are multiple variants with HiZ????  Right now it will return null
        if (selectedAdc.variants && selectedAdc.variants.length) return null;
        if (
            selectedAdc.hasVariant &&
            !selectedAdc.hasCurrentVariant &&
            selectedAdc.variant.code === Consts.VARIANT.ADC.HIZ
        ) {
            // find the HiZ part
            return this.partData.adcs.find(
                (adc) =>
                    adc.partCore === selectedAdc.partCore &&
                    adc.hasCurrentVariant &&
                    adc.currentVariant.code === Consts.VARIANT.ADC.HIZ,
            );
        } else if (
            (selectedAdc.hasVariant && !(selectedAdc.variant.code === Consts.VARIANT.ADC.HIZ)) ||
            !selectedAdc.hasVariant
        ) {
            // There is no hizVariant so don't return one
            return null;
        }

        return selectedAdc;
    }

    @computed('partData.adcs') get adcInputTypeOptions() {
        return this.partData.adcs.map((adc) => adc.inputType).removeDuplicates();
    }

    @computed('partData.drivers') get driverInputTypeOptions() {
        let intl = this.intl;
        return this.partData.drivers
            .map((driver) => {
                let type = intl.t(`driver_types.${driver.type.toLowerCase()}`).toString();
                if (type) {
                    return type;
                }
            })
            .removeDuplicates();
    }

    _findOneOptionId(optionsName, descriptionTranslation) {
        return this.get(optionsName).filter(
            (option) => option.description === this.intl.t(descriptionTranslation).toString(),
        )[0].id;
    }

    _findOneOption(optionsType, optionsFilter) {
        let foundOptions = this.get(`${optionsType}Options`).filter(optionsFilter);
        if (foundOptions && foundOptions.length === 1) {
            return foundOptions[0];
        }

        return this.get(`default${upperFirst(optionsType)}`);
    }

    findOneOptionById(optionsType, optionsId) {
        return this._findOneOption(optionsType, (option) => option.id === optionsId);
    }

    findOneOptionByDescription(optionsName, optionsDescription) {
        return this._findOneOption(optionsName, (option) => option.description === optionsDescription);
    }

    findOneOptionByPart(optionsName, optionsPart) {
        return this._findOneOption(optionsName, (option) => option.optionsPart === optionsPart);
    }

    findDefaultByOptionsType(optionsType) {
        return this.get(`default${upperFirst(optionsType)}`);
    }

    findSourceTypeFromAdcInputType(adcType) {
        if (adcType === Consts.ADC_TYPE.SE) {
            // Set sourceTypeInput to single ended and prevent differential
            return this.findOneOptionById('sourceType', this.sourceTypeSeId);
        } else {
            // Set sourceTypeInput to Differential but allow for Single-ended selection
            return this.findOneOptionById('sourceType', this.sourceTypeDiffId);
        }
    }
}
